import { useEffect, useRef } from 'react';

function FullPageCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    let width = 0;
    let height = 0;

    interface Node {
      x: number; y: number;
      vx: number; vy: number;
      radius: number;
      color: string;
      pulsePhase: number;
      pulseSpeed: number;
      isHub: boolean;
    }

    interface Particle {
      x: number; y: number;
      vx: number; vy: number;
      life: number;
      maxLife: number;
      size: number;
    }

    let nodes: Node[] = [];
    let particles: Particle[] = [];
    let time = 0;

    const COLORS = [
      'rgba(37,99,235,',
      'rgba(56,189,248,',
      'rgba(14,165,233,',
    ];

    function resize() {
      if (!canvas) return;
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width;
      canvas.height = height;
      initNodes();
    }

    function initNodes() {
      const count = Math.min(Math.floor((width * height) / 18000), 80);
      nodes = Array.from({ length: count }, (_, i) => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.28,
        vy: (Math.random() - 0.5) * 0.28,
        radius: i < count * 0.12 ? Math.random() * 2.5 + 1.5 : Math.random() * 1.4 + 0.6,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        pulsePhase: Math.random() * Math.PI * 2,
        pulseSpeed: Math.random() * 0.018 + 0.008,
        isHub: i < count * 0.12,
      }));
    }

    function spawnParticle() {
      if (particles.length < 50 && Math.random() < 0.25) {
        particles.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          life: 0,
          maxLife: Math.random() * 220 + 100,
          size: Math.random() * 1.2 + 0.3,
        });
      }
    }

    function drawGrid() {
      if (!ctx) return;
      const spacing = 90;
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(37,99,235,0.032)';
      ctx.lineWidth = 0.5;
      for (let x = 0; x < width; x += spacing) {
        ctx.moveTo(x, 0); ctx.lineTo(x, height);
      }
      for (let y = 0; y < height; y += spacing) {
        ctx.moveTo(0, y); ctx.lineTo(width, y);
      }
      ctx.stroke();
    }

    function draw() {
      if (!ctx || !canvas) return;
      time += 0.016;

      ctx.fillStyle = '#060a14';
      ctx.fillRect(0, 0, width, height);

      // Multiple radial glows spread across full height
      const glows = [
        { cx: 0.65, cy: 0.12, r: 0.45, c1: 'rgba(37,99,235,0.07)', c2: 'rgba(14,165,233,0.025)' },
        { cx: 0.2, cy: 0.35, r: 0.4, c1: 'rgba(56,189,248,0.05)', c2: 'transparent' },
        { cx: 0.8, cy: 0.6, r: 0.38, c1: 'rgba(37,99,235,0.06)', c2: 'transparent' },
        { cx: 0.15, cy: 0.75, r: 0.42, c1: 'rgba(56,189,248,0.04)', c2: 'transparent' },
        { cx: 0.55, cy: 0.88, r: 0.36, c1: 'rgba(37,99,235,0.05)', c2: 'transparent' },
      ];

      glows.forEach(({ cx, cy, r, c1, c2 }) => {
        const grd = ctx.createRadialGradient(
          width * cx, height * cy, 0,
          width * cx, height * cy, width * r,
        );
        grd.addColorStop(0, c1);
        grd.addColorStop(1, c2);
        ctx.fillStyle = grd;
        ctx.fillRect(0, 0, width, height);
      });

      drawGrid();

      nodes.forEach((n) => {
        n.x += n.vx; n.y += n.vy;
        n.pulsePhase += n.pulseSpeed;
        if (n.x < 0 || n.x > width) n.vx *= -1;
        if (n.y < 0 || n.y > height) n.vy *= -1;
      });

      // Edges
      const maxDist = 150;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < maxDist) {
            const alpha = (1 - dist / maxDist) * 0.3;
            const isHubEdge = nodes[i].isHub || nodes[j].isHub;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(37,99,235,${(isHubEdge ? alpha * 1.8 : alpha).toFixed(3)})`;
            ctx.lineWidth = isHubEdge ? 0.75 : 0.35;
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();

            if (isHubEdge && Math.random() < 0.0012) {
              const progress = (Math.sin(time * 2) + 1) / 2;
              const px = nodes[i].x + (nodes[j].x - nodes[i].x) * progress;
              const py = nodes[i].y + (nodes[j].y - nodes[i].y) * progress;
              ctx.beginPath();
              ctx.arc(px, py, 1.4, 0, Math.PI * 2);
              ctx.fillStyle = 'rgba(56,189,248,0.9)';
              ctx.fill();
            }
          }
        }
      }

      // Nodes
      nodes.forEach((n) => {
        const pulse = Math.sin(n.pulsePhase) * 0.4 + 0.6;
        const baseAlpha = n.isHub ? 0.92 : 0.65;

        if (n.isHub) {
          const glowGrd = ctx.createRadialGradient(n.x, n.y, n.radius, n.x, n.y, n.radius * 5);
          glowGrd.addColorStop(0, `${n.color}${(0.28 * pulse).toFixed(2)})`);
          glowGrd.addColorStop(1, `${n.color}0)`);
          ctx.beginPath();
          ctx.arc(n.x, n.y, n.radius * 5, 0, Math.PI * 2);
          ctx.fillStyle = glowGrd;
          ctx.fill();

          ctx.beginPath();
          ctx.arc(n.x, n.y, n.radius * 2.3, 0, Math.PI * 2);
          ctx.strokeStyle = `${n.color}${(0.38 * pulse).toFixed(2)})`;
          ctx.lineWidth = 0.7;
          ctx.stroke();
        }

        const dotGrd = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.radius * 1.5);
        dotGrd.addColorStop(0, `${n.color}${(baseAlpha * pulse).toFixed(2)})`);
        dotGrd.addColorStop(1, `${n.color}0)`);
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.radius * 1.5, 0, Math.PI * 2);
        ctx.fillStyle = dotGrd;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(n.x, n.y, n.radius, 0, Math.PI * 2);
        ctx.fillStyle = `${n.color}${baseAlpha.toFixed(2)})`;
        ctx.fill();
      });

      // Floating particles
      spawnParticle();
      particles = particles.filter((p) => p.life < p.maxLife);
      particles.forEach((p) => {
        p.x += p.vx; p.y += p.vy; p.life += 1;
        const t = p.life / p.maxLife;
        const alpha = t < 0.2 ? t / 0.2 : t > 0.8 ? (1 - t) / 0.2 : 1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${(alpha * 0.22).toFixed(2)})`;
        ctx.fill();
      });

      // Shooting stars
      if (Math.random() < 0.0025) {
        const sx = Math.random() * width;
        const sy = Math.random() * height;
        const len = Math.random() * 100 + 50;
        const angle = Math.PI / 4 + (Math.random() - 0.5) * 0.3;
        const streak = ctx.createLinearGradient(sx, sy, sx + Math.cos(angle) * len, sy + Math.sin(angle) * len);
        streak.addColorStop(0, 'rgba(56,189,248,0)');
        streak.addColorStop(0.4, 'rgba(56,189,248,0.65)');
        streak.addColorStop(1, 'rgba(56,189,248,0)');
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(sx + Math.cos(angle) * len, sy + Math.sin(angle) * len);
        ctx.strokeStyle = streak;
        ctx.lineWidth = 0.75;
        ctx.stroke();
      }

      animId = requestAnimationFrame(draw);
    }

    resize();
    window.addEventListener('resize', resize);
    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full pointer-events-none"
      style={{ display: 'block', zIndex: 0 }}
    />
  );
}

function StarField() {
  const stars = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    top: `${(i * 37 + 11) % 100}%`,
    left: `${(i * 53 + 7) % 100}%`,
    size: (i % 3) * 0.6 + 0.5,
    duration: 2 + (i % 3),
    delay: (i * 0.4) % 3,
    isCyan: i % 4 === 0,
  }));

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
      {stars.map((s) => (
        <div
          key={s.id}
          className="absolute rounded-full"
          style={{
            top: s.top,
            left: s.left,
            width: `${s.size}px`,
            height: `${s.size}px`,
            background: s.isCyan ? 'rgba(56,189,248,0.75)' : 'rgba(255,255,255,0.45)',
            animation: `twinkle ${s.duration}s ease-in-out infinite`,
            animationDelay: `${s.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

interface PageBackgroundProps {
  children: React.ReactNode;
}

export default function PageBackground({ children }: PageBackgroundProps) {
  return (
    <div className="relative min-h-screen" style={{ background: '#060a14' }}>
      <FullPageCanvas />
      <StarField />
      {/* Nebula glows — fixed, distributed vertically */}
      <div
        className="fixed rounded-full blur-3xl pointer-events-none"
        style={{
          width: '45%', height: '40%',
          top: '5%', right: '0%',
          background: 'radial-gradient(circle, rgba(37,99,235,0.07) 0%, transparent 70%)',
          zIndex: 0,
        }}
      />
      <div
        className="fixed rounded-full blur-3xl pointer-events-none"
        style={{
          width: '38%', height: '35%',
          top: '40%', left: '0%',
          background: 'radial-gradient(circle, rgba(56,189,248,0.05) 0%, transparent 70%)',
          zIndex: 0,
        }}
      />
      <div
        className="fixed rounded-full blur-3xl pointer-events-none"
        style={{
          width: '40%', height: '35%',
          bottom: '5%', right: '5%',
          background: 'radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)',
          zIndex: 0,
        }}
      />
      <div className="relative" style={{ zIndex: 1 }}>
        {children}
      </div>
    </div>
  );
}